import { useCallback, useEffect, useState } from "react";
import { Check, RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export const SecurityAlerts = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    setError("");
    try { const response = await api.get("/admin/security-alerts"); setData(response.data); }
    catch { setError("Security alerts could not be loaded. Please retry."); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const acknowledge = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/security-alerts/${id}/acknowledge`); await load(); }
    catch { setError("Could not acknowledge this alert. Please retry."); }
    finally { setBusy(null); }
  };
  return <section data-testid="admin-security-alerts" className="space-y-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h2 className="ff-cabinet text-lg font-bold" data-testid="security-alerts-title">Coupon security</h2>
        <p className="text-sm text-slate-600" data-testid="security-alert-open-count">{data ? `${data.open_count} open alerts` : "Loading alerts…"}</p></div>
      <Button variant="outline" onClick={load} className="gap-2" data-testid="security-alert-refresh"><RefreshCw className="h-4 w-4" />Refresh</Button>
    </div>
    {error && <p role="alert" className="text-sm text-red-700" data-testid="security-alert-error">{error}</p>}
    {data?.items.length === 0 && <p className="border-y py-8 text-sm text-slate-600" data-testid="security-alert-empty">No coupon alerts recorded.</p>}
    <div className="divide-y">{data?.items.map((item) => <article key={item.id} className="flex flex-wrap items-start gap-3 py-5" data-testid={`security-alert-${item.id}`}>
      <AlertTriangle className={`mt-1 h-5 w-5 shrink-0 ${item.severity === "medium" ? "text-amber-700" : "text-slate-500"}`} />
      <div className="min-w-0 flex-1"><h3 className="font-semibold" data-testid={`security-alert-title-${item.id}`}>{item.title}</h3>
        <p className="break-all text-sm text-slate-600" data-testid={`security-alert-actor-${item.id}`}>{item.actor}</p>
        <p className="mt-1 text-xs text-slate-500" data-testid={`security-alert-count-${item.id}`}>{item.count} {item.count === 1 ? "event" : "events"} · {new Date(item.updated_at).toLocaleString()} · {item.severity}</p>
      </div>
      {item.status === "open" ? <Button size="sm" variant="outline" disabled={!!busy} onClick={() => acknowledge(item.id)} data-testid={`security-alert-acknowledge-${item.id}`}>Acknowledge</Button>
        : <span className="flex items-center gap-1 text-xs text-emerald-700" data-testid={`security-alert-acknowledged-${item.id}`}><Check className="h-4 w-4" />Acknowledged</span>}
    </article>)}</div>
    {data && <div className="border-t pt-5 text-sm" data-testid="security-config-status">
      <h3 className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-4 w-4 text-emerald-700" />Application configuration validated</h3>
      <p className="mt-2 break-all text-slate-600" data-testid="security-public-base-url">Public links: {data.public_base_url}</p>
      <p className="mt-1 text-slate-600" data-testid="security-origin-count">{data.allowed_origins.length} explicit allowed origins</p>
    </div>}
  </section>;
};